import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  TextField,
  IconButton,
  Tooltip,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { CheckCircle, Cancel, Search, Edit } from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosConfig';

interface TILOpportunity {
  _id: string;
  personnel: {
    _id: string;
    name: string;
    rank?: string;
  };
  date: string;
  status: string;
  eventName?: string;
  shiftHours: number;
  standardHours: number;
  overtimeHours: number;
  rotaEntry: {
    _id: string;
    startTime: string;
    endTime: string;
  };
}

interface StaffMember {
  _id: string;
  name: string;
}

const TILDetectionTab: React.FC = () => {
  const [opportunities, setOpportunities] = useState<TILOpportunity[]>([]);
  const [allOpportunities, setAllOpportunities] = useState<TILOpportunity[]>([]); // For filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Date range - default to last 30 days
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });

  // Staff filter
  const [selectedStaff, setSelectedStaff] = useState<string>('all');
  const [staffList, setStaffList] = useState<StaffMember[]>([]);

  // Award Modal State
  const [awardModalOpen, setAwardModalOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState<TILOpportunity | null>(null);
  const [adjustedHours, setAdjustedHours] = useState<number>(0);
  const [awardReason, setAwardReason] = useState<string>('');

  // Dismiss Modal State
  const [dismissModalOpen, setDismissModalOpen] = useState(false);
  const [dismissReason, setDismissReason] = useState<string>('');
  const [opportunityToDismiss, setOpportunityToDismiss] = useState<TILOpportunity | null>(null);

  useEffect(() => {
    fetchOpportunities();
  }, []);

  useEffect(() => {
    // Filter opportunities when staff selection changes
    if (selectedStaff === 'all') {
      setOpportunities(allOpportunities);
    } else {
      setOpportunities(allOpportunities.filter(opp => opp.personnel._id === selectedStaff));
    }
  }, [selectedStaff, allOpportunities]);

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axiosInstance.get('/api/personnel/til-opportunities', {
        params: {
          startDate,
          endDate
        }
      });
      
      const fetchedOpportunities = response.data;
      setAllOpportunities(fetchedOpportunities);
      setOpportunities(fetchedOpportunities);
      
      // Extract unique staff members for filter dropdown
      const uniqueStaff = Array.from(
        new Map(
          fetchedOpportunities.map((opp: TILOpportunity) => [
            opp.personnel._id,
            { _id: opp.personnel._id, name: opp.personnel.name }
          ])
        ).values()
      ) as StaffMember[];
      
      setStaffList(uniqueStaff);
      setLoading(false);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch TIL opportunities');
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchOpportunities();
  };

  // Open Award Modal
  const handleOpenAwardModal = (opportunity: TILOpportunity) => {
    setSelectedOpportunity(opportunity);
    setAdjustedHours(opportunity.overtimeHours);
    setAwardReason('');
    setAwardModalOpen(true);
  };

  // Close Award Modal
  const handleCloseAwardModal = () => {
    setAwardModalOpen(false);
    setSelectedOpportunity(null);
    setAdjustedHours(0);
    setAwardReason('');
  };

  // Confirm Award with Adjustment
  const handleConfirmAward = async () => {
    if (!selectedOpportunity) return;

    try {
      console.log('🔥 AWARDING TIL:', selectedOpportunity.rotaEntry._id);
      
      // Send adjusted hours and reason to backend
      await axiosInstance.post(
        `/api/personnel/til-opportunities/${selectedOpportunity.rotaEntry._id}/award`,
        {
          adjustedHours: adjustedHours,
          reason: awardReason || undefined
        }
      );
      
      const wasAdjusted = adjustedHours !== selectedOpportunity.overtimeHours;
      const message = wasAdjusted
        ? `Awarded ${adjustedHours}h TIL to ${selectedOpportunity.personnel.name} (adjusted from ${selectedOpportunity.overtimeHours}h)`
        : `Awarded ${adjustedHours}h TIL to ${selectedOpportunity.personnel.name}`;
      
      setSuccessMessage(message);
      setTimeout(() => setSuccessMessage(null), 5000);
      
      // Remove from opportunities list
      setAllOpportunities(prev => 
        prev.filter(opp => opp.rotaEntry._id !== selectedOpportunity.rotaEntry._id)
      );
      setOpportunities(prev => 
        prev.filter(opp => opp.rotaEntry._id !== selectedOpportunity.rotaEntry._id)
      );
      
      handleCloseAwardModal();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to award TIL');
      setTimeout(() => setError(null), 3000);
      handleCloseAwardModal();
    }
  };

  // Open Dismiss Modal
  const handleOpenDismissModal = (opportunity: TILOpportunity) => {
    setOpportunityToDismiss(opportunity);
    setDismissReason('');
    setDismissModalOpen(true);
  };

  // Close Dismiss Modal
  const handleCloseDismissModal = () => {
    setDismissModalOpen(false);
    setOpportunityToDismiss(null);
    setDismissReason('');
  };

  // Confirm Dismiss
const handleConfirmDismiss = async () => {
  if (!opportunityToDismiss) return;

  try {
    await axiosInstance.post(
      `/api/personnel/til-opportunities/${opportunityToDismiss.rotaEntry._id}/dismiss`,
      { reason: dismissReason }
    );

    setAllOpportunities(prev =>
      prev.filter(opp => opp.rotaEntry._id !== opportunityToDismiss.rotaEntry._id)
    );
    setOpportunities(prev =>
      prev.filter(opp => opp.rotaEntry._id !== opportunityToDismiss.rotaEntry._id)
    );

    setSuccessMessage(
      `Dismissed TIL opportunity for ${opportunityToDismiss.personnel.name}. Reason: ${dismissReason || 'No reason provided'}`
    );
    setTimeout(() => setSuccessMessage(null), 5000);

    handleCloseDismissModal();
  } catch (err: any) {
    setError(err.response?.data?.message || 'Failed to dismiss opportunity');
    setTimeout(() => setError(null), 3000);
    handleCloseDismissModal();
  }
};

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {/* Date Range and Staff Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Staff Member</InputLabel>
            <Select
              value={selectedStaff}
              label="Staff Member"
              onChange={(e) => setSelectedStaff(e.target.value)}
            >
              <MenuItem value="all">All Staff</MenuItem>
              {staffList.map((staff) => (
                <MenuItem key={staff._id} value={staff._id}>
                  {staff.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={handleSearch}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {opportunities.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>
            All Reviewed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No TIL opportunities found for the selected date range
            {selectedStaff !== 'all' && ' and staff member'}
          </Typography>
        </Box>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {opportunities.length} TIL {opportunities.length === 1 ? 'opportunity' : 'opportunities'}
            {selectedStaff !== 'all' && ` for ${staffList.find(s => s._id === selectedStaff)?.name}`}
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Staff</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Shift Type</TableCell>
                  <TableCell>Actual Shift</TableCell>
                  <TableCell align="right">Standard</TableCell>
                  <TableCell align="right">Overtime</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {opportunities.map((opportunity) => (
                  <TableRow key={opportunity.rotaEntry._id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {opportunity.personnel.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opportunity.personnel.rank || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(opportunity.date).toLocaleDateString('en-GB')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={opportunity.eventName || opportunity.status}
                        size="small"
                        color="primary"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {opportunity.rotaEntry.startTime} - {opportunity.rotaEntry.endTime}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {opportunity.standardHours}h
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip
                        label={`+${opportunity.overtimeHours}h`}
                        size="small"
                        color="warning"
                        sx={{ fontWeight: 'bold' }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="Award TIL (with adjustment option)">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={() => handleOpenAwardModal(opportunity)}
                          >
                            <CheckCircle />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Dismiss (with reason)">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleOpenDismissModal(opportunity)}
                          >
                            <Cancel />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* Award/Adjust TIL Modal */}
      <Dialog open={awardModalOpen} onClose={handleCloseAwardModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Edit color="primary" />
            Award TIL with Adjustment
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedOpportunity && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Staff:</strong> {selectedOpportunity.personnel.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Date:</strong> {new Date(selectedOpportunity.date).toLocaleDateString('en-GB')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Shift:</strong> {selectedOpportunity.rotaEntry.startTime} - {selectedOpportunity.rotaEntry.endTime}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                <strong>System detected:</strong> {selectedOpportunity.overtimeHours}h overtime
              </Typography>

              <TextField
                label="Hours to Award"
                type="number"
                fullWidth
                value={adjustedHours}
                onChange={(e) => setAdjustedHours(parseFloat(e.target.value) || 0)}
                inputProps={{ min: 0, step: 0.5 }}
                helperText="Adjust if the detected hours are incorrect"
                sx={{ mb: 2 }}
              />

              <TextField
                label="Reason for Adjustment (optional)"
                multiline
                rows={3}
                fullWidth
                value={awardReason}
                onChange={(e) => setAwardReason(e.target.value)}
                placeholder="E.g., 'Staff finished early' or 'Shift extended due to emergency'"
                helperText="Provide context if you're adjusting the hours"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAwardModal}>Cancel</Button>
          <Button 
            onClick={handleConfirmAward} 
            variant="contained" 
            color="success"
            disabled={adjustedHours <= 0}
          >
            Confirm Award
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dismiss Modal */}
      <Dialog open={dismissModalOpen} onClose={handleCloseDismissModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Cancel color="error" />
            Dismiss TIL Opportunity
          </Box>
        </DialogTitle>
        <DialogContent>
          {opportunityToDismiss && (
            <Box sx={{ pt: 1 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Staff:</strong> {opportunityToDismiss.personnel.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Date:</strong> {new Date(opportunityToDismiss.date).toLocaleDateString('en-GB')}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Detected overtime:</strong> {opportunityToDismiss.overtimeHours}h
              </Typography>
              <Typography variant="body2" color="error" gutterBottom sx={{ mb: 3 }}>
                This opportunity will be dismissed and will not award any TIL.
              </Typography>

              <TextField
                label="Reason for Dismissal"
                multiline
                rows={3}
                fullWidth
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
                placeholder="E.g., 'Staff left early', 'Incorrect rota entry', 'Already compensated'"
                helperText="Required for audit trail"
                required
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDismissModal}>Cancel</Button>
          <Button 
            onClick={handleConfirmDismiss} 
            variant="contained" 
            color="error"
            disabled={!dismissReason.trim()}
          >
            Confirm Dismiss
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TILDetectionTab;